import { useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { GhostButton, ActionButton } from "./Primitives";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

/**
 * Approval pill + Approve/Reject action row for Incident / DAR / Maintenance
 * report detail views. Combines two concerns intentionally: (a) always show
 * the current approval status to any viewer, and (b) let supervisor+ roles
 * transition the status without a modal round-trip.
 *
 * Props:
 *   kind    "incident" | "dar" | "maintenance"
 *   report  the report doc (must have .id + .approval_status?)
 *   onChange (updated_status) => void — parent refreshes its state
 */
const APPROVER_ROLES = new Set([
  "admin", "super_admin", "supervisor",
  "operations_manager", "chief_security_officer",
  "dispatcher",
]);

const KIND_ENDPOINT = {
  incident:    "/incidents",
  dar:         "/dar",
  maintenance: "/maintenance-requests",
};

const STATUS_META = {
  pending:  { label: "PENDING",  color: "#F59E0B", bg: "#F59E0B22", Icon: Clock },
  approved: { label: "APPROVED", color: "#22C55E", bg: "#22C55E22", Icon: CheckCircle2 },
  rejected: { label: "REJECTED", color: "#EF4444", bg: "#EF444422", Icon: XCircle },
};

export function ApprovalPill({ status, testId }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={testId || `approval-pill-${status}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold font-mono"
      style={{ color: meta.color, backgroundColor: meta.bg }}
      title={`Approval status: ${meta.label}`}
    >
      <Icon className="size-3" strokeWidth={2.5} /> {meta.label}
    </span>
  );
}

export default function ApprovalActions({ kind, report, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  if (!report || !kind) return null;

  const canApprove = APPROVER_ROLES.has((user?.role || "").toLowerCase());
  const status = report.approval_status;
  const base = KIND_ENDPOINT[kind];
  if (!base) return null;

  const call = async (verb, notes) => {
    setBusy(verb);
    try {
      await api.post(`${base}/${report.id}/${verb}`, { notes: notes || null });
      toast.success(`Report ${verb === "approve" ? "approved" : "rejected"}`);
      onChange?.(verb === "approve" ? "approved" : "rejected");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
      setShowRejectBox(false);
      setRejectNote("");
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-4" data-testid={`approval-actions-${kind}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="label-mute">Approval</span>
          <ApprovalPill status={status || "pending"} />
          {report.approval_by_name && (
            <span className="text-[10px] text-zinc-500 font-mono">
              by {report.approval_by_name}
              {report.approval_at ? ` · ${new Date(report.approval_at).toLocaleString()}` : ""}
            </span>
          )}
        </div>
        {canApprove && (
          <div className="flex items-center gap-2">
            <ActionButton
              data-testid={`approval-approve-${kind}`}
              disabled={busy !== null || status === "approved"}
              onClick={() => call("approve", null)}
              className={status === "approved" ? "opacity-50 cursor-not-allowed" : ""}
            >
              <CheckCircle2 className="size-3.5" /> {busy === "approve" ? "Approving…" : "Approve"}
            </ActionButton>
            <GhostButton
              data-testid={`approval-reject-${kind}`}
              disabled={busy !== null || status === "rejected"}
              onClick={() => setShowRejectBox((v) => !v)}
              className={status === "rejected" ? "opacity-50 cursor-not-allowed" : ""}
            >
              <XCircle className="size-3.5" /> Reject
            </GhostButton>
          </div>
        )}
      </div>
      {report.approval_notes && (
        <div className="mt-3 text-xs text-zinc-400 border-l-2 border-[#27272A] pl-3" data-testid={`approval-notes-${kind}`}>
          <span className="label-mute">Note</span>
          <div className="mt-1 whitespace-pre-wrap">{report.approval_notes}</div>
        </div>
      )}
      {canApprove && showRejectBox && (
        <div className="mt-3" data-testid={`approval-reject-box-${kind}`}>
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Reason for rejection (required)"
            rows={2}
            className="w-full bg-transparent border border-[#27272A] px-3 py-2 text-sm outline-none focus:border-[#FF3B30]"
            data-testid={`approval-reject-note-${kind}`}
          />
          <div className="flex items-center gap-2 mt-2">
            <ActionButton
              data-testid={`approval-reject-confirm-${kind}`}
              disabled={busy !== null || !rejectNote.trim()}
              onClick={() => call("reject", rejectNote.trim())}
            >
              {busy === "reject" ? "Rejecting…" : "Confirm reject"}
            </ActionButton>
            <GhostButton onClick={() => { setShowRejectBox(false); setRejectNote(""); }}>
              Cancel
            </GhostButton>
          </div>
        </div>
      )}
    </div>
  );
}
